/**
 * Subscription Management Component
 * 
 * Displays current subscription details and allows management
 * of subscription (upgrade, cancel, add addons).
 */

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Card,
  Button,
  Tag,
  Descriptions,
  Spin,
  Alert,
  Modal,
  Divider,
  Row,
  Col,
  Progress,
  Typography,
  message,
  Input,
  InputNumber,
} from "antd";
import {
  CrownOutlined,
  CalendarOutlined,
  TeamOutlined,
  ShopOutlined,
  AppstoreOutlined,
  ReloadOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";

// Redux actions
import {
  fetchCurrentSubscription,
  fetchAvailablePlans,
  cancelSubscription,
  cancelPaystackSubscription,
  reactivateSubscription,
  addStaffToSubscription,
  addBranchToSubscription,
  previewPaystackUpgrade,
  addPaystackStaff,
  addPaystackBranches,
} from "../../../../redux/actions/subscriptionActions";

// Components
import NewSubscriptionModal from "../../../../components/StripePayment/NewSubscriptionModal";

import "./SubscriptionManagement.scss";

const { Title, Text } = Typography;

const SubscriptionManagement = ({
  user,
  subscription,
  allBranches,
  subscribedApps,
  allApps,
  fetchCurrentSubscription,
  fetchAvailablePlans,
  cancelSubscription,
  cancelPaystackSubscription,
  reactivateSubscription,
  addStaffToSubscription,
  addBranchToSubscription,
  previewPaystackUpgrade,
  addPaystackStaff,
  addPaystackBranches,
}) => {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Paystack cancel token modal state
  const [showCancelTokenModal, setShowCancelTokenModal] = useState(false);
  const [cancelToken, setCancelToken] = useState("");
  
  // Add staff/branch modal state
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [showAddBranchModal, setShowAddBranchModal] = useState(false);
  const [addQuantity, setAddQuantity] = useState(1);
  const [upgradePreview, setUpgradePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const { loading, currentSubscription, availablePlans, error } = subscription;

  const business = user?.employer || user;
  const currency = business?.currency || "USD";

  useEffect(() => {
    fetchCurrentSubscription();
    fetchAvailablePlans();
  }, []);

  // Check URL for subscription status
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const subscriptionStatus = urlParams.get("subscription");

    if (subscriptionStatus === "success") {
      message.success("Subscription activated successfully!");
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Refresh subscription data
      fetchCurrentSubscription();
    } else if (subscriptionStatus === "cancelled") {
      message.info("Subscription checkout was cancelled");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const formatCurrency = (amount, currencyCode = currency) => {
    const symbols = {
      USD: "$",
      GBP: "£",
      EUR: "€",
      NGN: "₦",
      GHS: "GH₵",
      KES: "KSh",
      ZAR: "R",
    };
    return `${symbols[currencyCode] || currencyCode} ${parseFloat(amount || 0).toFixed(2)}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      active: "green",
      trialing: "blue",
      past_due: "orange",
      cancelled: "red",
      canceled: "red",
      incomplete: "orange",
    };
    return colors[status?.toLowerCase()] || "default";
  };

  // Determine if this is a Paystack subscription
  const isPaystackSubscription = ["NGN", "GHS", "KES", "ZAR"].includes(currency) ||
    currentSubscription?.payment_provider === "paystack";

  // Handle cancel subscription - different flow for Stripe vs Paystack
  const handleCancelSubscription = () => {
    Modal.confirm({
      title: "Cancel Subscription",
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Are you sure you want to cancel your subscription?</p>
          <p>
            Your subscription will remain active until{" "}
            <strong>
              {moment(currentSubscription?.expires_on || currentSubscription?.current_period_end).format("MMMM D, YYYY")}
            </strong>
            .
          </p>
          {isPaystackSubscription && (
            <p style={{ color: "#ff4d4f", marginTop: 12 }}>
              <strong>Note:</strong> You will receive an email with a cancellation token.
            </p>
          )}
        </div>
      ),
      okText: "Yes, Cancel",
      okType: "danger",
      cancelText: "Keep Subscription",
      onOk: async () => {
        if (isPaystackSubscription) {
          // Paystack: Step 1 - Initiate cancellation (triggers email)
          setIsLoading(true);
          const result = await cancelPaystackSubscription(currentSubscription.id, null);
          setIsLoading(false);
          
          if (result) {
            if (result.message?.includes("email") || result.subscription?.status === "pending_cancellation") {
              // Email was sent, show token input modal
              message.info("Check your email for the cancellation token");
              setShowCancelTokenModal(true);
            } else if (result.subscription?.payment_status === "cancelled") {
              // Local cancellation succeeded (no Paystack subscription code)
              message.success("Subscription cancelled successfully");
              fetchCurrentSubscription();
            }
          }
        } else {
          // Stripe: Direct cancellation
          setIsLoading(true);
          const result = await cancelSubscription(currentSubscription.id, false, "user_requested");
          setIsLoading(false);
          if (result) {
            message.success("Subscription will be cancelled at the end of the billing period");
            fetchCurrentSubscription();
          }
        }
      },
    });
  };

  // Handle Paystack cancellation with email token (Step 2)
  const handleSubmitCancelToken = async () => {
    if (!cancelToken || cancelToken.length < 4) {
      message.error("Please enter a valid cancellation token");
      return;
    }
    
    setIsLoading(true);
    const result = await cancelPaystackSubscription(currentSubscription.id, cancelToken);
    setIsLoading(false);
    
    if (result) {
      if (result.subscription?.payment_status === "cancelled") {
        message.success("Subscription cancelled successfully");
        setShowCancelTokenModal(false);
        setCancelToken("");
        fetchCurrentSubscription();
      } else if (result.error) {
        message.error(result.error);
      }
    }
  };

  // Preview upgrade (add staff or branches)
  const handlePreviewUpgrade = async (type) => {
    setPreviewLoading(true);
    
    const data = type === "staff" 
      ? { add_staff: addQuantity }
      : { add_branches: addQuantity };
    
    const result = await previewPaystackUpgrade(data);
    setPreviewLoading(false);
    
    if (result?.preview) {
      setUpgradePreview(result.preview);
    }
  };

  // Open add staff modal
  const handleOpenAddStaffModal = () => {
    setAddQuantity(1);
    setUpgradePreview(null);
    setShowAddStaffModal(true);
  };

  // Open add branch modal
  const handleOpenAddBranchModal = () => {
    setAddQuantity(1);
    setUpgradePreview(null);
    setShowAddBranchModal(true);
  };

  // Confirm and add staff
  const handleConfirmAddStaff = async () => {
    setIsLoading(true);
    const result = await addPaystackStaff(currentSubscription.id, addQuantity);
    setIsLoading(false);
    
    if (result) {
      message.success(`Added ${addQuantity} staff member(s) successfully!`);
      setShowAddStaffModal(false);
      setUpgradePreview(null);
    }
  };

  // Confirm and add branches
  const handleConfirmAddBranches = async () => {
    setIsLoading(true);
    const result = await addPaystackBranches(currentSubscription.id, addQuantity);
    setIsLoading(false);
    
    if (result) {
      message.success(`Added ${addQuantity} branch(es) successfully!`);
      setShowAddBranchModal(false);
      setUpgradePreview(null);
    }
  };

  const handleReactivateSubscription = async () => {
    setIsLoading(true);
    const result = await reactivateSubscription(currentSubscription.id);
    setIsLoading(false);
    if (result) {
      message.success("Subscription reactivated successfully!");
      fetchCurrentSubscription();
    }
  };

  const handleRefresh = () => {
    fetchCurrentSubscription();
    fetchAvailablePlans();
  };

  // Calculate days remaining (inclusive of today)
  const getDaysRemaining = () => {
    if (!currentSubscription?.expires_on && !currentSubscription?.current_period_end) {
      return 0;
    }
    const expiryDate = moment(currentSubscription?.expires_on || currentSubscription?.current_period_end);
    const today = moment();
    // Add 1 to include today in the count
    return Math.max(0, expiryDate.diff(today, "days") + 1);
  };

  // Calculate total billing cycle days (from start to expiry, inclusive)
  const getTotalBillingCycleDays = () => {
    const createdAt = currentSubscription?.created_at || currentSubscription?.current_period_start;
    const expiresOn = currentSubscription?.expires_on || currentSubscription?.current_period_end;
    
    if (!createdAt || !expiresOn) {
      return 28; // Fallback to standard 28 days if dates not available
    }
    
    const startDate = moment(createdAt);
    const endDate = moment(expiresOn);
    // Add 1 to include both start and end days
    const totalDays = endDate.diff(startDate, "days") + 1;
    
    // Return at least 28 days to avoid division issues
    return Math.max(28, totalDays);
  };

  const daysRemaining = getDaysRemaining();
  const totalDays = getTotalBillingCycleDays();
  const progressPercent = Math.min(100, Math.round((daysRemaining / totalDays) * 100));

  // Render Free Plan state
  if (business?.subscription_plan === "Free" && !currentSubscription) {
    return (
      <div className="subscription-management">
        <Card className="subscription-card free-plan">
          <div className="free-plan-content">
            <CrownOutlined className="crown-icon" />
            <Title level={3}>You're on the Free Plan</Title>
            <Text type="secondary">
              Upgrade to unlock all features and grow your business!
            </Text>
            <Button
              type="primary"
              size="large"
              icon={<CrownOutlined />}
              onClick={() => setShowUpgradeModal(true)}
              style={{ marginTop: 24 }}
            >
              Upgrade Now
            </Button>
          </div>
        </Card>

        <NewSubscriptionModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          user={user}
        />
      </div>
    );
  }

  // Loading state
  if (loading && !currentSubscription) {
    return (
      <div className="subscription-management loading">
        <Spin size="large" />
        <Text>Loading subscription details...</Text>
      </div>
    );
  }

  // Error state
  if (error && !currentSubscription) {
    return (
      <div className="subscription-management">
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="subscription-management">
      {/* Subscription Overview Card */}
      <Card
        className="subscription-card"
        title={
          <div className="card-title">
            <CrownOutlined />
            <span>Current Subscription</span>
          </div>
        }
        extra={
          <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
            Refresh
          </Button>
        }
      >
        <Row gutter={[24, 24]}>
          {/* Plan Details */}
          <Col xs={24} md={12}>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Plan">
                <Tag color="blue" style={{ fontSize: 14 }}>
                  {currentSubscription?.pricing_plan?.name || business?.subscription_plan || "N/A"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(currentSubscription?.status || "active")}>
                  {(currentSubscription?.status || "active").toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Payment Provider">
                {currentSubscription?.payment_provider?.toUpperCase() || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Monthly Total">
                {formatCurrency(
                  currentSubscription?.monthly_total || currentSubscription?.pricing_plan?.price,
                  currentSubscription?.pricing_plan?.currency_code
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Auto Renew">
                <Tag color={currentSubscription?.auto_renew !== false ? "green" : "red"}>
                  {currentSubscription?.auto_renew !== false ? "Yes" : "No"}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Col>

          {/* Billing Period */}
          <Col xs={24} md={12}>
            <Card className="billing-period-card" size="small">
              <Title level={5}>
                <CalendarOutlined /> Billing Period
              </Title>
              <div className="period-dates">
                <div>
                  <Text type="secondary">Current Period Start</Text>
                  <Text strong>
                    {(currentSubscription?.current_period_start || currentSubscription?.created_at || business?.subscription_started_on)
                      ? moment(currentSubscription?.current_period_start || currentSubscription?.created_at || business?.subscription_started_on).format("MMM D, YYYY")
                      : "N/A"}
                  </Text>
                </div>
                <div>
                  <Text type="secondary">Renews On</Text>
                  <Text strong>
                    {(currentSubscription?.current_period_end || currentSubscription?.expires_on || business?.subscription_expires_on)
                      ? moment(currentSubscription?.current_period_end || currentSubscription?.expires_on || business?.subscription_expires_on).format("MMM D, YYYY")
                      : "N/A"}
                  </Text>
                </div>
              </div>
              <div className="days-remaining">
                <Progress
                  percent={progressPercent}
                  status={daysRemaining <= 5 ? "exception" : "active"}
                />
              </div>
            </Card>
          </Col>
        </Row>

        {/* Usage Section */}
        <Divider />
        <Title level={5}>Usage</Title>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Card className="usage-card extended" size="small">
              <TeamOutlined className="usage-icon" />
              <div className="usage-text">
                <Text type="secondary">Staff</Text>
                <Text strong>
                  {currentSubscription?.current_staff || allBranches?.flatMap((b) => b.staff || []).length || 0} /{" "}
                  {currentSubscription?.staff_included || "∞"}
                </Text>
              </div>
              {currentSubscription?.staff_included && currentSubscription?.staff_included !== "∞" && (
                <Progress
                  percent={Math.round(
                    ((currentSubscription?.current_staff || 0) / currentSubscription?.staff_included) * 100
                  )}
                  size="small"
                  status={
                    (currentSubscription?.current_staff || 0) >= currentSubscription?.staff_included
                      ? "exception"
                      : "active"
                  }
                  style={{ marginTop: 8 }}
                />
              )}
              {isPaystackSubscription && currentSubscription?.status === "active" && (
                <Button
                  type="link"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={handleOpenAddStaffModal}
                  style={{ marginTop: 8 }}
                >
                  Add Staff
                </Button>
              )}
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card className="usage-card extended" size="small">
              <ShopOutlined className="usage-icon" />
              <div className="usage-text">
                <Text type="secondary">Branches</Text>
                <Text strong>
                  {currentSubscription?.current_branches || allBranches?.length || 0} /{" "}
                  {currentSubscription?.branches_included || "∞"}
                </Text>
              </div>
              {currentSubscription?.branches_included && currentSubscription?.branches_included !== "∞" && (
                <Progress
                  percent={Math.round(
                    ((currentSubscription?.current_branches || 0) / currentSubscription?.branches_included) * 100
                  )}
                  size="small"
                  status={
                    (currentSubscription?.current_branches || 0) >= currentSubscription?.branches_included
                      ? "exception"
                      : "active"
                  }
                  style={{ marginTop: 8 }}
                />
              )}
              {isPaystackSubscription && currentSubscription?.status === "active" && (
                <Button
                  type="link"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={handleOpenAddBranchModal}
                  style={{ marginTop: 8 }}
                >
                  Add Branch
                </Button>
              )}
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card className="usage-card" size="small">
              <AppstoreOutlined className="usage-icon" />
              <div className="usage-text">
                <Text type="secondary">Products</Text>
                <Text strong>
                  {currentSubscription?.current_products || "N/A"} /{" "}
                  {currentSubscription?.products_allowed || "∞"}
                </Text>
              </div>
            </Card>
          </Col>

          {/* Active Addons in Usage Section */}
          {currentSubscription?.addons?.length > 0 && (
            <>
              {currentSubscription.addons.map((addon) => {
                // Find the app icon from subscribedApps or allApps
                const matchedSubscribedApp = subscribedApps?.find(
                  (sa) => sa.app?.app_name === addon.app_name || sa.app?.id === addon.app_id
                );
                const matchedApp = allApps?.find(
                  (a) => a.app_name === addon.app_name || a.id === addon.app_id
                );
                const appIcon = matchedSubscribedApp?.app?.icon || matchedApp?.icon || addon.icon;
                
                return (
                  <Col xs={12} sm={6} key={addon.id}>
                    <Card className="usage-card addon-usage-card" size="small">
                      {appIcon ? (
                        <img 
                          src={appIcon} 
                          alt={addon.app_name || addon.type} 
                          className="addon-icon"
                          style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'cover' }}
                        />
                      ) : (
                        <AppstoreOutlined className="usage-icon" style={{ color: "#d90068" }} />
                      )}
                      <div className="usage-text">
                        <Text type="secondary">{addon.app_name || addon.type?.toUpperCase()}</Text>
                        <Text strong>{formatCurrency(addon.unit_price)}</Text>
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </>
          )}
        </Row>

        {/* Actions */}
        <Divider />
        <div className="subscription-actions">
          <Button
            type="primary"
            icon={<CrownOutlined />}
            onClick={() => setShowUpgradeModal(true)}
          >
            Upgrade Plan
          </Button>

          {currentSubscription?.status === "active" &&
            currentSubscription?.cancel_at_period_end !== true && (
              <Button danger onClick={handleCancelSubscription} loading={isLoading}>
                Cancel Subscription
              </Button>
            )}

          {currentSubscription?.cancel_at_period_end === true && (
            <Button type="primary" onClick={handleReactivateSubscription} loading={isLoading}>
              Reactivate Subscription
            </Button>
          )}
        </div>

        {currentSubscription?.cancel_at_period_end && (
          <Alert
            message="Subscription Scheduled for Cancellation"
            description={`Your subscription will end on ${moment(
              currentSubscription?.expires_on || currentSubscription?.current_period_end
            ).format("MMMM D, YYYY")}. You can reactivate anytime before then.`}
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Card>

      {/* Upgrade Modal */}
      <NewSubscriptionModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        user={user}
      />

      {/* Paystack Cancel Token Modal */}
      <Modal
        title="Enter Cancellation Token"
        open={showCancelTokenModal}
        onCancel={() => {
          setShowCancelTokenModal(false);
          setCancelToken("");
        }}
        onOk={handleSubmitCancelToken}
        confirmLoading={isLoading}
        okText="Confirm Cancellation"
        okButtonProps={{ danger: true }}
      >
        <div style={{ marginBottom: 16 }}>
          <p>We've sent a cancellation token to your email. Please enter it below to confirm cancellation:</p>
          <Input
            placeholder="Enter 6-character token"
            value={cancelToken}
            onChange={(e) => setCancelToken(e.target.value.toUpperCase())}
            maxLength={10}
            style={{ fontSize: 18, letterSpacing: 4, textAlign: "center" }}
          />
        </div>
      </Modal>

      {/* Add Staff Modal */}
      <Modal
        title={<><TeamOutlined /> Add Staff Members</>}
        open={showAddStaffModal}
        onCancel={() => {
          setShowAddStaffModal(false);
          setUpgradePreview(null);
        }}
        footer={[
          <Button key="cancel" onClick={() => setShowAddStaffModal(false)}>
            Cancel
          </Button>,
          !upgradePreview && (
            <Button
              key="preview"
              type="primary"
              loading={previewLoading}
              onClick={() => handlePreviewUpgrade("staff")}
            >
              Preview Cost
            </Button>
          ),
          upgradePreview && (
            <Button
              key="confirm"
              type="primary"
              loading={isLoading}
              onClick={handleConfirmAddStaff}
            >
              Confirm & Pay {formatCurrency(upgradePreview.prorated_amount)}
            </Button>
          ),
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Text>How many staff members would you like to add?</Text>
          <div style={{ marginTop: 12 }}>
            <InputNumber
              min={1}
              max={50}
              value={addQuantity}
              onChange={(value) => {
                setAddQuantity(value);
                setUpgradePreview(null); // Clear preview when quantity changes
              }}
              size="large"
            />
          </div>
        </div>

        {upgradePreview && (
          <Card size="small" style={{ marginTop: 16, backgroundColor: "#f5f5f5" }}>
            <Title level={5}>Cost Preview</Title>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Quantity">
                {upgradePreview.breakdown?.staff?.quantity || addQuantity} staff
              </Descriptions.Item>
              <Descriptions.Item label="Unit Price">
                {formatCurrency(upgradePreview.breakdown?.staff?.unit_price || 0)}/month
              </Descriptions.Item>
              <Descriptions.Item label="Days Remaining">
                {upgradePreview.days_remaining} days
              </Descriptions.Item>
              <Descriptions.Item label="Prorated Amount (Today)">
                <Text strong type="success">
                  {formatCurrency(upgradePreview.prorated_amount)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="New Monthly Total">
                {formatCurrency(upgradePreview.new_monthly)}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}
      </Modal>

      {/* Add Branch Modal */}
      <Modal
        title={<><ShopOutlined /> Add Branches</>}
        open={showAddBranchModal}
        onCancel={() => {
          setShowAddBranchModal(false);
          setUpgradePreview(null);
        }}
        footer={[
          <Button key="cancel" onClick={() => setShowAddBranchModal(false)}>
            Cancel
          </Button>,
          !upgradePreview && (
            <Button
              key="preview"
              type="primary"
              loading={previewLoading}
              onClick={() => handlePreviewUpgrade("branches")}
            >
              Preview Cost
            </Button>
          ),
          upgradePreview && (
            <Button
              key="confirm"
              type="primary"
              loading={isLoading}
              onClick={handleConfirmAddBranches}
            >
              Confirm & Pay {formatCurrency(upgradePreview.prorated_amount)}
            </Button>
          ),
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Text>How many branches would you like to add?</Text>
          <div style={{ marginTop: 12 }}>
            <InputNumber
              min={1}
              max={20}
              value={addQuantity}
              onChange={(value) => {
                setAddQuantity(value);
                setUpgradePreview(null); // Clear preview when quantity changes
              }}
              size="large"
            />
          </div>
        </div>

        {upgradePreview && (
          <Card size="small" style={{ marginTop: 16, backgroundColor: "#f5f5f5" }}>
            <Title level={5}>Cost Preview</Title>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Quantity">
                {upgradePreview.breakdown?.branches?.quantity || addQuantity} branch(es)
              </Descriptions.Item>
              <Descriptions.Item label="Unit Price">
                {formatCurrency(upgradePreview.breakdown?.branches?.unit_price || 0)}/month
              </Descriptions.Item>
              <Descriptions.Item label="Days Remaining">
                {upgradePreview.days_remaining} days
              </Descriptions.Item>
              <Descriptions.Item label="Prorated Amount (Today)">
                <Text strong type="success">
                  {formatCurrency(upgradePreview.prorated_amount)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="New Monthly Total">
                {formatCurrency(upgradePreview.new_monthly)}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}
      </Modal>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
  subscription: state.subscription,
  allBranches: state.branch.allBranches,
  subscribedApps: state.appStore.subscribedApps,
  allApps: state.appStore.apps,
});

export default connect(mapStateToProps, {
  fetchCurrentSubscription,
  fetchAvailablePlans,
  cancelSubscription,
  cancelPaystackSubscription,
  reactivateSubscription,
  addStaffToSubscription,
  addBranchToSubscription,
  previewPaystackUpgrade,
  addPaystackStaff,
  addPaystackBranches,
})(SubscriptionManagement);
